import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays, Plus, Check, X, Trash2, Clock } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Avatar from '@/components/ui/Avatar';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import EmptyState from '@/components/ui/EmptyState';
import Spinner from '@/components/ui/Spinner';
import AppointmentForm from './AppointmentForm';
import styles from './Agenda.module.css';

const TYPE_VARIANT = { avaliacao: 'accent', aula: 'recovery', online: 'success', outro: 'neutral' };
const STATUS_VARIANT = { scheduled: 'neutral', done: 'success', canceled: 'danger' };

// Agrupa por dia (YYYY-MM-DD local), preservando ordem cronológica.
function groupByDay(items) {
  const groups = new Map();
  for (const a of items) {
    const d = new Date(a.scheduledAt);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(a);
  }
  return Array.from(groups.entries()).map(([key, list]) => ({
    key,
    date: new Date(list[0].scheduledAt),
    items: list.sort((x, y) => new Date(x.scheduledAt) - new Date(y.scheduledAt)),
  }));
}

export default function TrainerAgenda() {
  const { t, i18n } = useTranslation();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState('upcoming'); // upcoming | done | all

  const load = () => {
    setLoading(true);
    const params = {};
    if (filter === 'upcoming') params.status = 'scheduled';
    if (filter === 'done') params.status = 'done';
    api
      .get('/appointments', params)
      .then((res) => setAppointments((res.data || res).appointments || []))
      .catch(() => setAppointments([]))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [filter]);

  const create = async (payload) => {
    await api.post('/appointments', payload);
    setCreating(false);
    load();
  };

  const setStatus = async (a, status) => {
    try {
      await api.patch(`/appointments/${a.id}`, { status });
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const remove = async (a) => {
    if (!confirm(t('agenda.confirmDelete'))) return;
    try {
      await api.delete(`/appointments/${a.id}`);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const groups = useMemo(() => groupByDay(appointments), [appointments]);

  const fmtDay = (date) => {
    const today = new Date();
    const tmr = new Date();
    tmr.setDate(today.getDate() + 1);
    const sameDay = (a, b) => a.toDateString() === b.toDateString();
    if (sameDay(date, today)) return t('agenda.today');
    if (sameDay(date, tmr)) return t('agenda.tomorrow');
    return date.toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const fmtTime = (iso) =>
    new Date(iso).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });

  const filters = [
    { key: 'upcoming', label: t('agenda.filterUpcoming') },
    { key: 'done', label: t('agenda.filterDone') },
    { key: 'all', label: t('agenda.filterAll') },
  ];

  return (
    <>
      <PageHeader kicker={t('agenda.kicker')} title={t('agenda.title')}>
        <Button icon={Plus} onClick={() => setCreating(true)}>{t('agenda.new')}</Button>
      </PageHeader>

      <div className={styles.filters}>
        {filters.map((f) => (
          <button
            key={f.key}
            type="button"
            className={filter === f.key ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
          <Spinner />
        </div>
      ) : groups.length === 0 ? (
        <Card padding="none">
          <EmptyState
            icon={CalendarDays}
            title={t('agenda.emptyTitle')}
            description={t('agenda.emptyDesc')}
            action={<Button icon={Plus} onClick={() => setCreating(true)}>{t('agenda.new')}</Button>}
          />
        </Card>
      ) : (
        <div className={styles.days}>
          {groups.map((g) => (
            <div key={g.key} className={styles.day}>
              <div className={styles.dayHead}>{fmtDay(g.date)}</div>
              <div className={styles.list}>
                {g.items.map((a) => (
                  <Card key={a.id} padding="md" className={styles.appt}>
                    <div className={styles.time}>
                      <Clock size={13} strokeWidth={2} />
                      {fmtTime(a.scheduledAt)}
                    </div>
                    <Avatar name={a.counterpart?.name} src={a.counterpart?.avatar} size={36} />
                    <div className={styles.apptMain}>
                      <div className={styles.apptName}>{a.counterpart?.name || '—'}</div>
                      {a.notes && <div className={styles.apptNotes}>{a.notes}</div>}
                    </div>
                    <div className={styles.badges}>
                      <Badge variant={TYPE_VARIANT[a.type] || 'neutral'}>
                        {t(`agenda.types.${a.type}`, a.type)}
                      </Badge>
                      <Badge variant={STATUS_VARIANT[a.status] || 'neutral'}>
                        {t(`agenda.status.${a.status}`, a.status)}
                      </Badge>
                    </div>
                    <div className={styles.actions}>
                      {a.status === 'scheduled' && (
                        <>
                          <button
                            type="button"
                            className={styles.act}
                            onClick={() => setStatus(a, 'done')}
                            aria-label={t('agenda.markDone')}
                            title={t('agenda.markDone')}
                          >
                            <Check size={15} strokeWidth={2} />
                          </button>
                          <button
                            type="button"
                            className={styles.act}
                            onClick={() => setStatus(a, 'canceled')}
                            aria-label={t('agenda.cancelAppt')}
                            title={t('agenda.cancelAppt')}
                          >
                            <X size={15} strokeWidth={2} />
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        className={`${styles.act} ${styles.del}`}
                        onClick={() => remove(a)}
                        aria-label={t('agenda.delete')}
                        title={t('agenda.delete')}
                      >
                        <Trash2 size={15} strokeWidth={2} />
                      </button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={creating} onClose={() => setCreating(false)} kicker={t('agenda.new')} title={t('agenda.title')} size="md">
        <AppointmentForm onSubmit={create} onCancel={() => setCreating(false)} />
      </Modal>
    </>
  );
}
